// Shared creative-identity and status vocabulary for the By creative table and
// its same-page detail state (issue 13). Kept framework-free so both the
// rendered breakdown and the detail panel resolve identity and status the same
// way.
import type { BreakdownRow } from "./breakdown-sort";

// The stable reporting identity: provider + provider account id + provider
// entity id. The display name is only a fallback for a genuinely absent id, so
// two same-name creatives in different accounts never collapse to one key. This
// mirrors the server's grouping key in buildEntityBreakdown.
export function creativeEntityKey(
  row: Pick<BreakdownRow, "platform" | "accountId" | "adId" | "campaignId" | "name">,
): string {
  return `${row.platform}:${row.accountId}:${row.adId ?? row.campaignId ?? row.name}`;
}

export function entityTypeLabel(entityType: BreakdownRow["entityType"]): "Ad" | "Asset" {
  return entityType === "asset" ? "Asset" : "Ad";
}

export function platformLabel(platform: BreakdownRow["platform"]): string {
  return platform === "tiktok" ? "TikTok" : platform === "meta" ? "Meta" : "Google";
}

// Normalized provider entity status (see server marketing/provider-status.ts).
// Duplicated as a string union here so the client renders a status label without
// importing server code; the server is the single source of the values.
export const entityStatusValues = [
  "active",
  "paused",
  "not_delivering",
  "in_review",
  "rejected",
  "deleted",
  "unknown",
] as const;

export type EntityStatusValue = (typeof entityStatusValues)[number];

const STATUS_LABEL: Record<EntityStatusValue, string> = {
  active: "Active",
  paused: "Paused",
  not_delivering: "Not delivering",
  in_review: "In review",
  rejected: "Rejected",
  deleted: "Deleted",
  unknown: "Unknown",
};

export function entityStatusLabel(status: EntityStatusValue | null | undefined): string {
  if (!status) return "Unknown";
  return STATUS_LABEL[status] ?? "Unknown";
}

// A calm tone bucket for the status pill: a genuinely delivering/active status
// reads positive, a stopped or rejected one reads muted, everything else stays
// neutral. Deliberately not a judgement — just enough contrast to scan.
export function entityStatusTone(
  status: EntityStatusValue | null | undefined,
): "positive" | "muted" | "neutral" {
  if (status === "active") return "positive";
  if (status === "paused" || status === "rejected" || status === "deleted") return "muted";
  return "neutral";
}
